import {
  Box,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ADToBS } from "bikram-sambat-js";
import BaseModal from "components/Shared/BaseModel/BaseModel";
import React, { useRef } from "react";
import ShipmentInvoicePrint from "./ShipmentInvoicePrint";
import { formatNumWithCommas } from "utils/formatString";
import nctLogo from "../../../../../assets/nctLogo2.png";

const numberToWords = (num) => {
  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100); // Get cents

  const units = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const thousands = ["", "Thousand", "Million"];
  const convertLessThanThousand = (num) => {
    if (num === 0) return "";
    if (num < 20) return units[num];
    if (num < 100) {
      return (
        tens[Math.floor(num / 10)] + (num % 10 ? " " + units[num % 10] : "")
      );
    }
    return (
      units[Math.floor(num / 100)] +
      " Hundred" +
      (num % 100 ? " " + convertLessThanThousand(num % 100) : "")
    );
  };

  let word = "";
  let thousandIndex = 0;
  let tempNum = integerPart;

  if (tempNum === 0) {
    word = "Zero";
  } else {
    while (tempNum > 0) {
      if (tempNum % 1000 !== 0) {
        word =
          convertLessThanThousand(tempNum % 1000) +
          (thousands[thousandIndex] ? " " + thousands[thousandIndex] : "") +
          (word ? " " + word : "");
      }
      tempNum = Math.floor(tempNum / 1000);
      thousandIndex++;
    }
  }

  if (decimalPart > 0) {
    word += " and " + convertLessThanThousand(decimalPart) + " Cents";
  }

  return word || "Zero";
};

const ShipmentInvoicePrint2 = ({
  isOpen,
  onClose,
  ShipmentDetail,
  pricesRes,
  entity,
  loggedInUser,
}) => {
  const printRef = useRef();

  const formatDate = (date) => {
    const adDateString = date.toLocaleDateString("en-CA").replace(/-/g, "/");
    const bsDate = ADToBS(adDateString); // Convert to BS date
    return {
      miti: bsDate,
      time: date.toLocaleTimeString("en-GB", { hour12: false }),
      date: date.toLocaleDateString("en-GB").split("/").reverse().join("/"),
    };
  };

  const pickupTime = ShipmentDetail?.shipmentPickupTime
    ? new Date(ShipmentDetail.shipmentPickupTime)
    : new Date();

  const { miti, time, date } = formatDate(pickupTime);

  const getWeightLabel = (s) => {
    if (s?.weight) return `${s.weight} Kg`;
    if (s?.unit?.value && s?.unit?.unitType?.name) {
      return s.unit.unitType.name.toLowerCase() === "weight"
        ? `${s.unit.value} Kg`
        : `${s.unit.value} ${s.unit.unitType.name}`;
    }
    return "N/A";
  };

  const getWeightValue = (s) => {
    if (s?.weight) return s.weight;
    if (s?.unit?.value) return s.unit.value;
    return 1;
  };

  const shipmentInvoice = (
    shipment,
    pricesRes,
    entity,
    loggedInUser,
    loadingPrices
  ) => {
    ShipmentInvoicePrint.printStatic({
      shipment,
      pricesRes,
      loadingPrices,
      loggedInUser,
      entity,
    });
  };

  const sender = ShipmentDetail?.customerId
    ? {
      name: ShipmentDetail?.customerId?.customerUser?.name || "",
    }
    : {
      name: ShipmentDetail?.vendor?.shopName || "",
    };

  const VAT_RATE = 0.13;
  const roundMoney = (value) => Number(Number(value || 0).toFixed(2));
  const getDescription = (item) => {
    const name = item?.feeItem?.name || "Shipment Charge";
    if (name === "BASE - Standard charge") return "Service Charge";
    if (name.toLowerCase().includes("freight")) return "Freight Charge";
    if (name.toLowerCase().includes("documentation")) return "Documentation & Processing Fee";
    return name;
  };
  const particulars = pricesRes?.data?.length
    ? pricesRes.data
      .filter((item) => item.chargeType !== "Cash on Delivery")
      .map((item) => ({
        description: getDescription(item),
        amount: roundMoney(item.charge),
        rate: roundMoney(Number(item.charge || 0) / (1 + VAT_RATE)),
        vat: roundMoney(Number(item.charge || 0) - roundMoney(Number(item.charge || 0) / (1 + VAT_RATE))),
      }))
    : [
      {
        description: "Shipment Charge",
        rate: 0.0,
        amount: 0.0,
      },
    ];

  const handlePrint = () => {
    if (!ShipmentDetail) {
      console.log("No shipment data available");
      return;
    }

    try {
      shipmentInvoice(ShipmentDetail, pricesRes, entity, loggedInUser);
    } catch (error) {
      console.error("Print failed:", error);
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} width="1100px">
      <Paper ref={printRef} elevation={1}>
        <div
          style={{
            padding: "16px",
            fontSize: "11px",
            fontFamily: "Arial, sans-serif",
          }}
        >
          {/* Header */}

          <Box className="b-2 flex w-full items-center justify-between">
            <Box className="mr-4 flex items-center">
              <img
                src={entity?.logo?.url || nctLogo}
                alt="Logo"
                className="h-[120px] w-[120px] object-contain"
              />
              <Box className="ml-4">
                <Typography
                  variant="h6"
                  className="text-[16px] font-bold text-red-600"
                >
                  {entity?.name || ""}
                </Typography>
                <Typography variant="body2" className="text-gray-600">
                  {entity?.location || ""}
                </Typography>
              </Box>
            </Box>

            <Box className="flex flex-col items-end pr-3">
              <Typography variant="body2" className="text-gray-600">
                Tel No.: {entity?.telNo || ""}
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                VAT No.: {entity?.additionalInfo?.vatNumber?.value || ""}
              </Typography>
            </Box>
          </Box>

          <Box className="mb-2 flex items-center justify-center bg-gray-50 p-2">
            <Typography variant="h6" className="text-[12px] font-bold">
              Sales Receipt
            </Typography>
          </Box>

          {/* Invoice Info */}
          <Grid container spacing={1} className="mb-2 w-full text-[11px]">
            {/* First Row */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <strong>Receipt No:</strong> {ShipmentDetail?.receiptNumber || ""}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <strong>Shipment ID:</strong> {ShipmentDetail?.shipmentId || ""}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <strong>Mode:</strong> To Pay
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <strong>Miti:</strong> {miti}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <strong>Time:</strong> {time}
              </Box>
            </Grid>

            {/* Second Row */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <strong>Customer:</strong> {sender?.name || ""}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <strong>VAT No:</strong>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <strong>Date:</strong> {date || ""}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>&nbsp;</Box> {/* Empty cell for alignment */}
            </Grid>
          </Grid>

          <hr className="my-2 border-t border-gray-400" />

          {/* Sender/Receiver Info */}
          <Grid container spacing={1} className="mb-2 w-full text-[11px]">
            {/* Row 1 */}
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <strong>Sender:</strong> {sender.name || ""}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <strong>VAT No:</strong>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <strong>From:</strong>{" "}
                {ShipmentDetail?.sourceBranch?.name || "--"}
              </Box>
            </Grid>

            {/* Row 2 */}
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <strong>Receiver:</strong>{" "}
                {ShipmentDetail?.receiver?.customerUser?.name || ""}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box>&nbsp;</Box> {/* Empty cell for alignment */}
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <strong>To:</strong>{" "}
                {ShipmentDetail?.destinationBranch?.name || ""}
              </Box>
            </Grid>

            {/* Row 3 */}
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <strong>Phone:</strong> {ShipmentDetail?.receiver?.phone || ""}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <strong>Address:</strong>{" "}
                {ShipmentDetail?.receiver?.address || ""}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <strong>G.Value:</strong>
              </Box>
            </Grid>
          </Grid>

          <hr className="my-2 border-t border-gray-400" />

          {/* Product Table */}
          <Table size="small" className="mb-4 w-full text-[11px]">
            <TableHead>
              <TableRow className="border-y">
                <TableCell className="p-1 font-bold">SN.</TableCell>
                <TableCell className="p-1 font-bold">Particular</TableCell>
                <TableCell className="p-1 font-bold">
                  {ShipmentDetail?.weight
                    ? "Weight"
                    : ShipmentDetail?.unit?.unitType?.name?.toLowerCase() === "weight"
                      ? "Weight"
                      : ShipmentDetail?.unit?.value
                        ? "Unit"
                        : "Weight"}
                </TableCell>
                <TableCell className="p-1 font-bold">Rate</TableCell>
                <TableCell className="p-1 text-right font-bold">
                  Taxable Amt
                </TableCell>
                <TableCell className="p-1 text-right font-bold">
                  VAT 13%
                </TableCell>
                <TableCell className="p-1 text-right font-bold">
                  Total Amt
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {particulars.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="p-1">{index + 1}</TableCell>
                  <TableCell className="p-1">{item.description}</TableCell>
                  <TableCell className="p-1">
                    {index === 0 ? getWeightLabel(ShipmentDetail) : ""}
                  </TableCell>
                  <TableCell className="p-1">
                    Rs {formatNumWithCommas(
                      (item?.rate / getWeightValue(ShipmentDetail)).toFixed(2)
                    ) || "0.00"}
                  </TableCell>
                  <TableCell className="p-1 text-right">
                    Rs. {formatNumWithCommas(item?.rate?.toFixed(2)) || "0.00"}
                  </TableCell>
                  <TableCell className="p-1 text-right">
                    Rs. {formatNumWithCommas(item?.vat?.toFixed(2)) || "0.00"}
                  </TableCell>
                  <TableCell className="p-1 text-right">
                    Rs.{" "}
                    {formatNumWithCommas(item?.amount?.toFixed(2)) || "0.00"}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t border-black">
                <TableCell className="p-1" colSpan={6}>
                  <strong>Total</strong>
                </TableCell>
                {/* <TableCell className="p-1 text-right">
                  <strong>
                    Rs.{" "}
                    {formatNumWithCommas(
                      particulars
                        .reduce((acc, item) => acc + item?.rate, 0.0)
                        .toFixed(2)
                    ) || "0.00"}
                  </strong>
                </TableCell> */}
                <TableCell className="p-1 text-right">
                  <strong>
                    Rs.{" "}
                    {formatNumWithCommas(
                      particulars
                        .reduce((sum, item) => sum + item?.amount, 0.0)
                        .toFixed(2)
                    ) || "0.00"}
                  </strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Totals: source charges are VAT-inclusive, so taxable amount is
              back-calculated per line with amount / 1.13. */}
          <Box className="mb-2 text-right">
            <p>Total Taxable Amount: <strong>Rs. {formatNumWithCommas(particulars.reduce((sum, item) => sum + item.rate, 0).toFixed(2))}</strong></p>
            <p>VAT 13%: <strong>Rs. {formatNumWithCommas(particulars.reduce((sum, item) => sum + item.vat, 0).toFixed(2))}</strong></p>
            <p>
              <strong>Grand Total:</strong>{" "}
              <strong>
                Rs.{" "}
                {formatNumWithCommas(
                  particulars
                    .reduce((sum, item) => sum + item?.amount, 0.0)
                    .toFixed(2)
                ) || "0.00"}
              </strong>
            </p>
            <Typography variant="caption">Formula: Base Amount = Original Amount ÷ 1.13; Base Amount + VAT = Original Amount.</Typography>
          </Box>

          {/* Footer */}
          <Box className="mb-2">
            <p>
              <strong>In words:</strong>{" "}
              {numberToWords(
                Math.round(
                  particulars.reduce((sum, item) => sum + item?.amount, 0)
                )
              )}{" "}
              Rupees Only.
            </p>
            <hr className="my-1 border border-dotted border-gray-300" />
            <p className="mb-4 mt-2">
              <strong>Remarks:</strong> {ShipmentDetail?.remarks || ""}
            </p>
          </Box>

          {/* Footer Bottom */}
          <Box className="mt-6 flex items-center justify-between text-[11px]">
            <div></div>
            <div className="text-right">
              <p>
                <strong>Print Time:</strong>{" "}
                {new Date().toLocaleString("en-US", {
                  dateStyle: "medium",
                  timeStyle: "medium",
                })}
              </p>
              <p className="mt-1">
                <strong>Booking Officer:</strong> {loggedInUser?.name || "N/A"}
              </p>
            </div>
          </Box>

          <div className="mt-4 text-[8px]">
            <p className="mt-1">
              <strong>Note:</strong> This is a system generated sales receipt
              and not a final tax invoice. This document is electronically
              generated and does not require stamp or signature.
            </p>
          </div>

          <div className="mt-4 text-center text-[12px] font-semibold">
            <p className="mt-1">
              Thank you for choosing {entity?.name || ""} as your logistics
              partner!
            </p>
          </div>
        </div>
      </Paper>

      <Button variant="contained" onClick={handlePrint} sx={{ mt: 2 }}>
        Print
      </Button>
    </BaseModal>
  );
};

export default ShipmentInvoicePrint2;
